import React from "react";
import { MediaFieldsFragment } from "@/gql/graphql";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { cn } from "@/lib/utils";

type AvatarProps = {
  square?: boolean;
  initials?: string;
  alt?: string;
  className?: string;
  objectFit?: "cover" | "contain";
} & { media?: MediaFieldsFragment | null; src?: string };

export function Avatar({
  media,
  src,
  square = false,
  initials,
  alt = "",
  className,
  objectFit = "cover",
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<"span">) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={cn(
        // Basic layout
        "inline-grid shrink-0 align-middle [--avatar-radius:20%] [--ring-opacity:20%] *:col-start-1 *:row-start-1",
        "outline-solid outline-black/(--ring-opacity) dark:outline-white/(--ring-opacity) outline-1 -outline-offset-1",
        // Add the correct border radius
        square
          ? "rounded-(--avatar-radius) *:rounded-(--avatar-radius)"
          : "overflow-hidden rounded-full *:rounded-full",
        className,
      )}
    >
      {initials && (
        <svg
          className="size-full select-none fill-current p-[5%] text-[48px] font-medium uppercase"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : "true"}
        >
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em"
          >
            {initials}
          </text>
        </svg>
      )}
      {media || src ? (
        <ImoovaImage
          src={src}
          alt={alt}
          media={media ?? undefined}
          className={cn(
            "size-full",
            objectFit === "contain" ? "object-contain" : "object-cover",
          )}
        />
      ) : null}
    </span>
  );
}
